import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import * as Sentry from "@sentry/angular";
import { BehaviorSubject, concat, EMPTY, Observable, of, ReplaySubject } from "rxjs";
import { catchError, filter, map, switchMap, takeUntil, tap } from "rxjs/operators";
import { USER_CONFIG } from "../user-config";
import { SiteOption, UserInformation, UsersMeResponse } from "./user-typing";

/** Stable demo site id — matches the demo repositories' seeded data. */
const DEMO_SITE_ID = "00000000-0000-4000-8000-000000000010";

/**
 * Default user info used when no OAuth / backend is available (dev mode).
 */
function defaultUserInfo(): UserInformation {
	const stored = typeof localStorage !== "undefined" ? localStorage.getItem("siteIdOption") : null;
	const siteId = stored || DEMO_SITE_ID;
	return {
		name: "Max Mustermann",
		email: "dev@partikelart.local",
		activeSiteId: siteId,
		siteOptions: [{ siteId, siteName: "Default Site", accountId: "00000000-0000-4000-8000-000000000001" }],
	};
}

@Injectable({
	providedIn: "root",
})
export class UserService {
	// 1. Dependency Injection
	private readonly http = inject(HttpClient);
	private readonly config = inject(USER_CONFIG);
	/** Optional: hosts without a router (tests) skip onboarding redirects. */
	private readonly router = inject(Router, { optional: true });

	// 2. State Management
	private firstLogin = true;

	private userInformationSubject = new BehaviorSubject<UserInformation>({
		name: "",
		email: "",
		activeSiteId: "",
	});

	private currentSiteOptionSelectionSubject = new ReplaySubject<SiteOption>(1);

	// 3. Public Observables
	public readonly currentSiteOptionSelection$ = this.currentSiteOptionSelectionSubject.asObservable();

	public readonly userInformationObservable$ = this.userInformationSubject
		.asObservable()
		.pipe(filter((userinfo) => !!userinfo.activeSiteId));

	public readonly currentSiteId$ = concat(
		this.userInformationObservable$.pipe(
			takeUntil(this.currentSiteOptionSelection$),
			map((info) => info.activeSiteId),
		),
		this.currentSiteOptionSelection$.pipe(map((site) => site.siteId)),
	);

	/**
	 * Attempts to resolve user information from multiple sources:
	 * 1. Backend API (GET /api/users/me, identity domain) — preferred source
	 * 2. Fallback defaults (dev/demo mode)
	 */
	private resolveUserInfo(): Observable<UserInformation> {
		// Try backend first if portalBackendUrl is configured and not in demo mode
		const backendUrl = this.config.portalBackendUrl || this.config.url;
		if (backendUrl && !this.config.demo) {
			return this.tryBackendSource(backendUrl).pipe(tap((info) => this.processUserLogic(info)));
		}

		return this.resolveFromFallback();
	}

	/**
	 * Try GET /api/users/me — the identity-domain profile endpoint.
	 * Falls back to defaults on any error (network, unauthenticated, etc.).
	 */
	private tryBackendSource(backendUrl: string): Observable<UserInformation> {
		return this.http.get<UsersMeResponse>(`${backendUrl}/api/users/me`).pipe(
			map((backendData) => {
				const siteOptions = this.buildSiteOptions(backendData);
				if (siteOptions.length === 0) {
					// Authenticated but no account membership: onboarding case (D2).
					return null as unknown as UserInformation;
				}
				const activeSite = siteOptions.find((s) => s.siteId === backendData.activeSiteId) ?? siteOptions[0];

				return {
					name: backendData.name || "",
					email: backendData.email || "",
					activeSiteId: activeSite.siteId,
					siteOptions,
					backendData,
				} as UserInformation;
			}),
			switchMap((userInfo: UserInformation | null) => {
				if (userInfo) {
					return of(userInfo);
				}
				if (this.config.demo) {
					return this.resolveFromFallback();
				}
				// Real deployment: NEVER fabricate a demo site (writes against it 404).
				// Send the user to onboarding to create their account + first site.
				this.router?.navigate(["/register"]);
				return EMPTY;
			}),
			catchError(() => {
				Sentry.addBreadcrumb({
					message: "GET /api/users/me failed, falling back to defaults",
					level: "warning",
				});
				return this.resolveFromFallback();
			}),
		);
	}

	/**
	 * Sites the user can select: explicit site permissions first; accounts'
	 * default sites fill in for members without per-site rows (D6).
	 */
	private buildSiteOptions(me: UsersMeResponse): SiteOption[] {
		const options = new Map<string, SiteOption>();
		for (const sp of me.sitePermissions ?? []) {
			options.set(sp.siteId, { siteId: sp.siteId, siteName: sp.siteName, accountId: sp.accountId });
		}
		for (const membership of me.accounts ?? []) {
			if (membership.active && membership.accountDefaultSiteId && !options.has(membership.accountDefaultSiteId)) {
				options.set(membership.accountDefaultSiteId, {
					siteId: membership.accountDefaultSiteId,
					siteName: membership.accountDisplayName,
					accountId: membership.accountId,
				});
			}
		}
		return [...options.values()];
	}

	/**
	 * Fallback resolution: default user info for development / demo.
	 */
	private resolveFromFallback(): Observable<UserInformation> {
		return of(defaultUserInfo()).pipe(tap((info) => this.processUserLogic(info)));
	}

	getUserInfoFromClaims(): Observable<UserInformation> {
		return this.resolveUserInfo();
	}

	/**
	 * Internal logic to handle side effects like LocalStorage
	 */
	private processUserLogic(userInfo: UserInformation): void {
		if (userInfo.siteOptions && userInfo.siteOptions.length > 0) {
			const defaultOption =
				userInfo.siteOptions.find((option) => option.siteId === userInfo.activeSiteId) || userInfo.siteOptions[0];
			this.currentSiteOptionSelectionSubject.next(defaultOption);

			if (this.config.demo) {
				localStorage.setItem("siteIdOption", defaultOption.siteId);
			}
		}

		this.userInformationSubject.next(userInfo);
		this.firstLogin = false;
	}

	getAndSetUser(): void {
		this.getUserInfoFromClaims().subscribe();
	}

	isFirstLogin(): boolean {
		return this.firstLogin;
	}

	updateSiteOptionSelection(siteOption: SiteOption): void {
		if (this.config.demo) {
			localStorage.setItem("siteIdOption", siteOption.siteId);
		}
		this.currentSiteOptionSelectionSubject.next(siteOption);
	}

	/**
	 * Keep this for cases where you specifically need to force a
	 * refresh of user data from the API.
	 */
	getUserInfo(): Observable<UserInformation> {
		return this.resolveUserInfo();
	}

	setActiveSite(siteId: string): Observable<{ success: true; activeSiteId: string }> {
		const backendUrl = this.config.portalBackendUrl || this.config.url;
		return this.http.patch<{ success: true; activeSiteId: string }>(`${backendUrl}/api/users/me/active-site`, {
			siteId,
		});
	}

	requestRepeatTest(pv_id: string, repeatFormData: any): Observable<any> {
		return this.http.post<any>(`${this.config.url}/v1/customers/specs/${pv_id}/repeat`, repeatFormData);
	}
}
